import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanySearchBoxComponent } from '../../companies/company-search-box/company-search-box.component';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { TrainingAndEducation } from '../../../models/trainingAndEducation';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AutocompleteSelectComponent } from '../../shared/autocomplete-select/autocomplete-select.component';
import { EducationField } from '../../../models/educationField';
import { University } from '../../../models/university';
import { Country } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { Observable, map } from 'rxjs';
import { DirectorTrainingOrEducationType } from '../../../models/enums/directorTrainingOrEducationType.enum';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-training-education-selection-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    ReactiveFormsModule,
    AutocompleteSelectComponent,
    DragDropModule],
  templateUrl: './training-education-selection-dialog.component.html',
  styleUrl: './training-education-selection-dialog.component.scss'
})
export class TrainingEducationSelectionDialogComponent implements OnInit, AfterViewInit {
  private lutService = inject(LutService);
  private lookupService = inject(LookupDataService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);

  @ViewChild('educationFieldAutocomplete') educationFieldAutocomplete?: AutocompleteSelectComponent;
  @ViewChild('universityAutocomplete') universityAutocomplete?: AutocompleteSelectComponent;

  selectedTrainingAndEducation: TrainingAndEducation | null = null;
  DirectorTrainingOrEducationType = DirectorTrainingOrEducationType;
  trainingEducationTypes = [
    { value: DirectorTrainingOrEducationType.BSc, label: 'B.Sc.' },
    { value: DirectorTrainingOrEducationType.MSc, label: 'M.Sc.' },
    { value: DirectorTrainingOrEducationType.PhD, label: 'PhD' }
  ];

  countries: Country[] = [];
  newUniversityCountryControl = new FormControl<Country | null>(null);
  isEditMode: boolean;

  constructor(
    public dialogRef: MatDialogRef<TrainingEducationSelectionDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: TrainingAndEducation | null,
  ) {
    this.isEditMode = !!data;
    this.selectedTrainingAndEducation = data
      ? {
          id: data.id,
          type: data.type,
          educationField: data.educationField,
          university: data.university
        }
      : {
          id: crypto.randomUUID(),
          type: DirectorTrainingOrEducationType.None,
          educationField: undefined,
          university: undefined
        };
  }

  ngOnInit(): void {
    this.lookupService.getTableStream<Country>('countries').subscribe(data => {
      if (data) this.countries = [...data].sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  ngAfterViewInit(): void {
    if (!this.data) return;
    queueMicrotask(() => {
      const educationFieldName = this.data?.educationField?.name ?? '';
      const universityName = this.data?.university?.name ?? '';
      this.educationFieldAutocomplete?.searchControl.setValue(educationFieldName, { emitEvent: false });
      this.universityAutocomplete?.searchControl.setValue(universityName, { emitEvent: false });
    });
  }

  compareCountryById = (a: Country | null, b: Country | null): boolean => !!a && !!b && a.id === b.id;

  searchEducationFields = (searchTerm: string): Observable<EducationField[]> => {
    return this.lutService.searchEducationFields({
      pageNumber: 1,
      pageSize: 1000,
      sortBy: 'name',
      ascending: true,
      name: searchTerm
    }).pipe(
      map(response => {
        const term = (searchTerm ?? '').trim().toLowerCase();
        if (!term) return response.data;
        const exact: EducationField[] = [];
        const rest: EducationField[] = [];
        for (const e of response.data) {
          ((e.name ?? '').toLowerCase() === term ? exact : rest).push(e);
        }
        return exact.length ? [...exact, ...rest] : response.data;
      })
    );
  };

  searchUniversities = (searchTerm: string): Observable<University[]> => {
    return this.lutService.searchUniversities({
      pageNumber: 1,
      pageSize: 1000,
      sortBy: 'name',
      ascending: true,
      name: searchTerm
    }).pipe(
      map(response => {
        const term = (searchTerm ?? '').trim().toLowerCase();
        if (!term) return response.data;
        const exact: University[] = [];
        const rest: University[] = [];
        for (const u of response.data) {
          ((u.name ?? '').toLowerCase() === term ? exact : rest).push(u);
        }
        return exact.length ? [...exact, ...rest] : response.data;
      })
    );
  };

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    this.dialogRef.close(this.selectedTrainingAndEducation);
  }

  onTypeSelected(event: any): void {
    this.selectedTrainingAndEducation!.type = event.value;
  }

  onEducationFieldSelected(educationField: EducationField): void {
    this.selectedTrainingAndEducation!.educationField = educationField;
  }

  onUniversitySelected(university: University): void {
    this.selectedTrainingAndEducation!.university = university;
  }

  get isFormValid(): boolean {
    return !!(
      this.selectedTrainingAndEducation?.type !== undefined &&
      this.selectedTrainingAndEducation?.type !== DirectorTrainingOrEducationType.None &&
      this.selectedTrainingAndEducation?.educationField &&
      this.selectedTrainingAndEducation?.university
    );
  }

  get canAddNewEducationField(): boolean {
    const ac = this.educationFieldAutocomplete;
    if (!ac) return false;
    if (ac.isLoading) return false;

    const value = (ac.searchControl.value ?? '').toString().trim();
    if (value.length < 3) return false;

    const selected = this.selectedTrainingAndEducation?.educationField;
    if (selected && selected.name === value) return false;

    const valueLower = value.toLowerCase();
    const exactMatch = ac.filteredOptions.some(o => (o?.name ?? '').toLowerCase() === valueLower);
    if (exactMatch) return false;

    return ac.filteredOptions.length === 0 || !ac.isPanelOpen;
  }

  onAddNewEducationField(): void {
    const ac = this.educationFieldAutocomplete;
    const name = (ac?.searchControl.value ?? '').toString().trim();
    if (!name) return;

    this.confirmationDialogService.openConfirmationDialog(
      'Add Education Field',
      `No education field matched "${name}". Add it as a new education field?`,
      'Add',
      'Cancel',
      'add',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.lutService.createEducationField({ name, systemReserved: false }).subscribe({
        next: (created) => {
          this.selectedTrainingAndEducation!.educationField = created;
          if (this.educationFieldAutocomplete) {
            this.educationFieldAutocomplete.filteredOptions = [created];
            this.educationFieldAutocomplete.totalFilteredCount = 1;
            this.educationFieldAutocomplete.searchControl.setValue(created.name, { emitEvent: false });
          }
          this.toast.success(`Education field "${created.name}" added`, {
            iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
          });
        },
        error: (error) => {
          console.error('Error creating education field:', error);
          this.toast.error('Failed to add education field');
        }
      });
    });
  }

  get canAddNewUniversity(): boolean {
    const ac = this.universityAutocomplete;
    if (!ac) return false;
    if (ac.isLoading) return false;

    const value = (ac.searchControl.value ?? '').toString().trim();
    if (value.length < 3) return false;

    const selected = this.selectedTrainingAndEducation?.university;
    if (selected && selected.name === value) return false;

    const valueLower = value.toLowerCase();
    const exactMatch = ac.filteredOptions.some(o => (o?.name ?? '').toLowerCase() === valueLower);
    if (exactMatch) return false;

    return ac.filteredOptions.length === 0 || !ac.isPanelOpen;
  }

  onAddNewUniversity(): void {
    const ac = this.universityAutocomplete;
    const name = (ac?.searchControl.value ?? '').toString().trim();
    const country = this.newUniversityCountryControl.value;
    if (!name || !country) return;

    this.confirmationDialogService.openConfirmationDialog(
      'Add University',
      `No university matched "${name}". Add it as a new university in ${country.name}?`,
      'Add',
      'Cancel',
      'add',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.lutService.createUniversity({
        name,
        countryId: country.id,
        countryName: country.name,
        systemReserved: false
      }).subscribe({
        next: (created) => {
          this.selectedTrainingAndEducation!.university = created;
          if (this.universityAutocomplete) {
            this.universityAutocomplete.filteredOptions = [created];
            this.universityAutocomplete.totalFilteredCount = 1;
            this.universityAutocomplete.searchControl.setValue(created.name, { emitEvent: false });
          }
          this.newUniversityCountryControl.reset();
          this.toast.success(`University "${created.name}" added`, {
            iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
          });
        },
        error: (error) => {
          console.error('Error creating university:', error);
          this.toast.error('Failed to add university');
        }
      });
    });
  }
}
